import {ConfirmationDialog, dateTime, DetailColumns, DetailPane, FormDialog} from '@management-ui/core';
import {Archive, CopyAll, Edit, Upload} from '@mui/icons-material';
import {Alert, Box, Paper, Snackbar} from '@mui/material';
import React, {useCallback, useContext} from 'react';
import {useHistory} from 'react-router-dom';
import {ServiceContext} from '../../../../components/Services';
import YesNo from '../../../../components/YesNo';
import {truncate} from '../../../../formatters';
import routes from '../../../../routes';
import AuditTypeForm from '../../forms/AuditTypeForm';
import Sections from './Sections';
import {reverse} from 'named-urls';

const TypeDetail = ({type, loading, onLoading, openDialogs, toggleDialog, toggleDialogs, onSaved}) => {
  const services = useContext(ServiceContext);
  const history = useHistory();

  const handleConfirmDelete = useCallback((confirmed) => {
    toggleDialogs({deleteType: false, deleteQuestions: confirmed});
  }, [toggleDialogs]);

  const handleDelete = useCallback((confirmed) => {
    toggleDialog('deleteQuestions', false);
    onLoading(true);
    services.auditType.type.deleteType(type, confirmed).then(() => {
      onLoading(false);
      history.push(routes.auditTypes.index);
    }).catch(() => onLoading(false));
  }, [services, type, onLoading, toggleDialog, history]);

  const handleUpdate = useCallback((confirmed) => {
    toggleDialog('updateAudits', false);
    if (confirmed) {
      onLoading(true);
      services.auditType.type.updateAudits(type).then(() => {
        onLoading(false);
        toggleDialog('auditsUpdated', true);
      }).catch(() => onLoading(false));
    }
  }, [services, type, onLoading, toggleDialog]);

  const handleCopy = useCallback((confirmed) => {
    toggleDialog('copyType', false);
    if (confirmed) {
      onLoading(true);
      services.auditType.type.copyType(type).then((copied) => {
        onLoading(false);
        history.push(reverse(routes.auditTypes.detail, {id: copied.id}));
      }).catch(() => onLoading(false));
    }
  }, [services, type, onLoading, toggleDialog, history]);

  return (
    <>
      <DetailColumns columns={[

        <DetailPane
          title="Category Details"
          actions={[
            {title: 'Edit Category', icon: <Edit/>, onClick: () => toggleDialog('editType', true)},
            {title: 'Copy Category', icon: <CopyAll/>, onClick: () => toggleDialog('copyType', true)},
            {title: 'Update Audits', icon: <Upload/>, onClick: () => toggleDialog('updateAudits', true)},
            {title: 'Archive Category', icon: <Archive/>, onClick: () => toggleDialog('deleteType', true)},
          ]}
          details={[
            {title: 'Title', value: type.title},
            {title: 'Introduction', value: type.introduction ? truncate(type.introduction) : '-'},
            {title: 'Show exceeded ratings?', value: <YesNo value={type.show_exceeded}/>},
          ]}
          dialogs={[
            (props) => (
              <FormDialog
                {...props}
                title="Edit Category"
                open={openDialogs['editType'] ?? false}
                onClose={() => toggleDialog('editType', false)}
                render={(props) => (
                  <AuditTypeForm
                    {...props}
                    type={type}
                    onSaved={onSaved}/>
                )}
              />
            ),
          ]}
        />,

        <DetailPane
          title="Admin Details"
          details={[
            {title: 'Created', value: dateTime(type.created_at)},
            {title: 'Last Updated', value: dateTime(type.updated_at)}
          ]}
        />,
      ]}/>

      <Paper marginTop={2} width="100%" component={Box}>
        <Sections type={type} loading={loading} onUpdated={onSaved}/>
      </Paper>

      <ConfirmationDialog
        title="Archive Category"
        message="Are you sure you want to archive this category?"
        open={openDialogs['deleteType']}
        onClose={handleConfirmDelete}
      />

      <ConfirmationDialog
        title="Archive Associated Questions"
        message="Would you like to move any questions which are only used in this category?"
        open={openDialogs['deleteQuestions']}
        onClose={handleDelete}
      />

      <ConfirmationDialog
        title="Update Audits"
        message={`Are you sure you want to rollout all changes made to this category to any unfinished '${type.title}' audits?`}
        open={openDialogs['updateAudits']}
        onClose={handleUpdate}
      />

      <ConfirmationDialog
        title="Copy Category"
        message="Would you like to create a copy of this category? The copy will have the same sections and be linked to the same questions"
        open={openDialogs['copyType']}
        onClose={handleCopy}
      />

      <Snackbar
        open={openDialogs['auditsUpdated']}
        autoHideDuration={4000}
        onClose={() => toggleDialog('auditsUpdated', false)}
        anchorOrigin={{vertical: 'bottom', horizontal: 'center'}}
      >
        <Alert onClose={() => toggleDialog('auditsUpdated', false)} severity="success">Unfinished '{type.title}' Audits
          Updated</Alert>
      </Snackbar>
    </>
  );
};

export default TypeDetail;
